/**
 * Printing - browser implementation.
 *
 * Resolved by webpack for the web bundle (printer.ts is used on Android/iOS).
 * Public API is identical, so call sites never branch on Platform.
 *
 * Receipts are rendered into an off-screen iframe and printed with
 * `window.print()`, which keeps the POS UI off the paper and lets the receipt
 * carry its own @page sizing. That is the right behaviour for both a real
 * receipt printer installed as a Windows printer and for "Save as PDF".
 */

import RNPrintWeb from '../shims/reactNativePrint';

export type PrintHtmlOptions = {
  isLandscape?: boolean;
  printerURL?: string;
  jobName?: string;
};

export type SelectedPrinter = {url: string; name: string};

export type SaveAndPrintOptions = {
  base64: string;
  fileName: string;
  cleanupAfterPrint?: boolean;
};

/**
 * The browser's own print dialog owns printer selection, so the app must not
 * try to show a chooser of its own.
 */
export const supportsPrinterSelection = false;

/** Receipt width for an 80mm thermal roll; 58mm rolls print fine inside this. */
const RECEIPT_PAGE_CSS = `
  @page { size: 80mm auto; margin: 3mm; }
  @media print {
    html, body { width: 80mm; margin: 0; padding: 0; }
  }
`;

/**
 * Receipt HTML built for Android is sized for a narrow roll already; adding an
 * explicit @page rule stops Chrome/Edge defaulting to A4 and shrinking it.
 */
function withReceiptPageSize(html: string): string {
  const styleTag = `<style>${RECEIPT_PAGE_CSS}</style>`;
  if (html.includes('</head>')) {
    return html.replace('</head>', `${styleTag}</head>`);
  }
  return `${styleTag}${html}`;
}

export async function printHtml(html: string, options: PrintHtmlOptions = {}): Promise<void> {
  await RNPrintWeb.print({
    html: options.isLandscape ? html : withReceiptPageSize(html),
    isLandscape: options.isLandscape ?? false,
  });
}

/** `filePath` on web is a URL (blob:, data: or https:). */
export async function printFile(filePath: string, _options: PrintHtmlOptions = {}): Promise<void> {
  await RNPrintWeb.print({filePath});
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], {type: mimeType});
}

function mimeTypeFor(fileName: string): string {
  const extension = fileName.split('.').pop()?.toLowerCase();
  if (extension === 'pdf') {
    return 'application/pdf';
  }
  if (extension === 'png') {
    return 'image/png';
  }
  if (extension === 'jpg' || extension === 'jpeg') {
    return 'image/jpeg';
  }
  return 'application/octet-stream';
}

/**
 * Browsers cannot write to a chosen directory, so "save and print" becomes
 * "hand the document to the browser": the file is downloaded (so the operator
 * keeps a copy, matching the Android behaviour) and opened in a new tab where
 * the built-in PDF viewer offers Print.
 *
 * @returns the object URL the document was served from.
 */
export async function saveAndPrintDocument(options: SaveAndPrintOptions): Promise<string> {
  const {base64, fileName} = options;
  const blob = base64ToBlob(base64, mimeTypeFor(fileName));
  const objectUrl = URL.createObjectURL(blob);

  // Keep a copy on disk, like the native implementation does.
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  // Then open it so the operator can print straight away.
  window.open(objectUrl, '_blank', 'noopener');

  // The tab needs the URL to stay alive; release it well after it has loaded.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);

  return objectUrl;
}

/** No app-level printer chooser in a browser - the print dialog handles it. */
export async function selectPrinter(): Promise<SelectedPrinter | null> {
  return null;
}
